import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import ActionType from "../../resources/enums";
import ROUTES from "../../constants/routes";
import storage from "../../util/storage";
import { persistor } from "../../state_management/reducers";
import { site as siteApi } from "../../services/site";
import { currentUser } from "../../services/auth";
import { trakingCodeGenrator } from "../../util/helper";
import CommonModal from "../commonModel/CommonModel";
import CommonButton from "../utilities/formElements/commonButton/CommonButton";

/*
 * 🔴 UNWRAP THE JSON-RPC ENVELOPE.
 *
 * http.ts's interceptor returns withData(res.data) = { error, data }, and for
 * an RPC call `data` is the envelope { jsonrpc, id, result }. Reading
 * `res.data` therefore yields the ENVELOPE, not the payload.
 *
 * Getting this wrong reported a SUCCESS as a failure: the site really was
 * created, the modal said "Could not add that website", and the natural
 * response is to try again - creating a second one. Worse than an error.
 */
const rpcResult = (res: any): any =>
  res?.data?.result ?? res?.result ?? res?.data;

/*
 * Adding a site, and the install that actually makes it work.
 *
 * 🔴 THE URL IS THE EASY PART. Typing it takes five seconds; installing the
 * snippet is the task that decides whether the customer ever sees a bubble.
 * So this does not create the site and walk away - it hands over the snippet
 * for THAT site and stays open until the site reports itself connected.
 *
 * 🔴 IT DOES NOT SWITCH AUTOMATICALLY. An earlier version did, and that drops
 * someone on an empty dashboard for a site with no tracking - which is
 * indistinguishable from a broken install, and is exactly how a person
 * concludes the product does not work.
 *
 * 🔴 THREE STEPS, AND THE DASHBOARD IS GATED ON STEP 3. Add, install, connect.
 * "Go to the new site" only becomes available once the site has actually
 * reported data, so the first thing anyone sees on a new dashboard is a
 * working one.
 *
 * 🔴 IT IS PORTALLED TO document.body. This component is mounted inside the
 * HEADER, and the app's other modals (TrialFinishModel and friends) are
 * mounted at the top of AnalyticsView. That is the whole difference: rendered
 * in place, the modal is trapped in the header's stacking and overflow
 * context and comes out as a clipped strip a few pixels tall. CommonModal
 * does not portal itself - it relies on being mounted somewhere sane.
 *
 * 🔴 IT USES THE APP'S OWN CommonModal, NOT A BESPOKE SHELL. The first
 * version hand-rolled a backdrop and panel, and rendered a foot wide: a
 * transformed ancestor in the header captured its position:fixed, so the
 * backdrop sized itself to the header instead of the viewport. That is the
 * same mistake as TrafficIndicator's hand-rolled tooltip - building a second
 * implementation of something the app already has. CommonModal is already
 * mounted correctly and already carries the app's modal language.
 *
 * 🔴 BUT IT NEVER TRAPS ANYONE. "Finish later" is available at every step. A
 * customer who cannot paste a script right now - no access, needs a developer
 * - must be able to leave without losing the site they just created. The
 * snippet stays recoverable from Settings for that site.
 */

const POLL_MS = 4000;

interface Props {
  onClose: () => void;
}

const AddSiteModal: React.FC<Props> = ({ onClose }) => {
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const dispatch = useDispatch();

  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<any>(null);
  const [connected, setConnected] = useState(false);
  const [copied, setCopied] = useState(false);

  /*
   * Poll for the per-site flag once the site exists.
   *
   * Deliberately the SITE's trackingCodeInstalled, not the user's. The user
   * flag turns true the moment any site connects, so on a multi-site account
   * it would report a brand-new site as live before a single event arrived.
   */
  useEffect(() => {
    if (!created?._id || connected) {
      return;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const res: any = await siteApi({
          jsonprc: "2.0",
          method: "getSite",
          params: { siteId: created._id },
          id: 0,
        } as any);
        const fresh = rpcResult(res);
        if (!cancelled && fresh?.trackingCodeInstalled) {
          setConnected(true);
        }
      } catch (e) {
        // A failed poll is not worth surfacing - the next one runs in 4s.
      }
    };

    check();
    const id = window.setInterval(check, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [created, connected]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed || busy) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const res: any = await siteApi({
        jsonprc: "2.0",
        method: "addSite",
        params: { userId: authReducer?.authData?._id, siteUrl: trimmed },
        id: 0,
      } as any);

      const site = rpcResult(res);
      if (!site?._id) {
        /*
         * The server's own wording is shown verbatim - it already says the
         * useful thing ("Your plan allows 3 websites. Upgrade to track
         * more."), and a second wording here would drift from it.
         */
        setError(
          res?.error?.message ??
            res?.data?.error?.message ??
            "Could not add that website.",
        );
        setBusy(false);
        return;
      }

      // Refresh so authData.sites carries the new site before anything tries
      // to switch to it.
      const fresh: any = await currentUser();
      if (fresh?.data) {
        dispatch({ type: ActionType.LOGIN, payload: fresh.data });
      }

      setCreated(site);
      setBusy(false);
    } catch (err: any) {
      setError(err?.message ?? "Could not add that website.");
      setBusy(false);
    }
  };

  const goToNewSite = async () => {
    if (!created?._id) {
      return;
    }
    // Storage BEFORE the dispatch: the LOGIN reducer prefers the site named
    // by storage.siteId, so dispatching first would have it read the previous
    // id and undo the switch.
    storage.set("siteId", created._id);
    dispatch({
      type: ActionType.LOGIN,
      payload: { ...authReducer.authData, site: created },
    });
    try {
      await persistor.flush();
    } catch (e) {
      // A failed flush must not trap the user mid-switch.
    }
    window.location.assign(ROUTES.ANALYTICS_VIEW.replace("/:id", ""));
  };

  const snippet = created?._id ? trakingCodeGenrator(created._id) : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setError("Could not copy. Select the code and copy it manually.");
    }
  };

  return createPortal(
    <CommonModal
      title={created ? `Set up ${created.host}` : "Add a website"}
      onClose={onClose}
      className="add-site-modal"
    >
      <div className="add-site-body">
        <ol className="add-site-steps" aria-hidden="true">
          <li className={!created ? "is-current" : "is-done"}>1. Add</li>
          <li className={!created ? "" : connected ? "is-done" : "is-current"}>
            2. Install
          </li>
          <li className={connected ? "is-current" : ""}>3. Connect</li>
        </ol>

        {!created ? (
          <>
            <p className="add-site-lead">
              Track another site from the same account. Your page-view
              allowance is shared across all of them.
            </p>
            <form onSubmit={submit} className="add-site-form">
              <input
                type="text"
                autoFocus
                value={url}
                placeholder="example.com"
                disabled={busy}
                onChange={(e) => setUrl(e.target.value)}
              />
            </form>
            {error && <p className="add-site-error">{error}</p>}

            {/* Bottom button group.
                🔴 `button-align-modal` ONLY. It is already
                `display:flex; justify-content:center; gap:10px`.
                Do NOT add `flex-align-modal-btn` - despite the name it is
                `flex-direction: column`, a modifier for a different layout,
                and adding it stacks the buttons vertically (shipped once,
                2026-09-26). */}
            <div className="button-align-modal mt-4 mb-3">
              <CommonButton
                type="button"
                label="Cancel"
                className="theme-btn success-outline-btn transparent text-uppercase fontBold"
                onClick={onClose}
              />
              <CommonButton
                type="submit"
                label={busy ? "Adding..." : "Add website"}
                className="theme-btn success-btn text-uppercase fontBold"
                disabled={busy || !url.trim()}
                loading={busy}
                onClick={submit}
              />
            </div>
          </>
        ) : (
          <>
            <p className="add-site-lead">
              Paste this into the <code>&lt;head&gt;</code> of every page on{" "}
              {created.host}. It is one script tag and loads nothing from
              anywhere else.
            </p>

            <pre className="add-site-snippet">{snippet}</pre>
            <button type="button" className="add-site-copy" onClick={copy}>
              {copied ? "Copied" : "Copy code"}
            </button>

            <p
              className={
                connected ? "add-site-status is-live" : "add-site-status"
              }
            >
              <span className="add-site-dot" />
              {connected
                ? "Connected. We have received data from this site."
                : "Waiting for the first visit. This updates on its own."}
            </p>

            {/* 🔴 An exit at every step - see the note at the top of this
                file on never trapping someone who cannot install right now.
                Back returns to step 1; the site created so far is kept, so
                going back is safe rather than destructive. */}
            <div className="button-align-modal mt-4 mb-3">
              <CommonButton
                type="button"
                label="Back"
                className="theme-btn success-outline-btn transparent text-uppercase fontBold"
                onClick={() => setCreated(null)}
              />
              <CommonButton
                type="button"
                label="Finish later"
                className="theme-btn success-outline-btn transparent text-uppercase fontBold"
                onClick={onClose}
              />
              <CommonButton
                type="button"
                label={connected ? `Go to ${created.host}` : "Waiting for data..."}
                className="theme-btn success-btn text-uppercase fontBold"
                disabled={!connected}
                onClick={goToNewSite}
              />
            </div>

            {!connected && (
              <p className="add-site-note">
                The dashboard opens once this site reports its first visit, so
                you do not land on an empty one. You can close this and come
                back to the code from Settings at any time.
              </p>
            )}
            {error && <p className="add-site-error">{error}</p>}
          </>
        )}
      </div>
    </CommonModal>,
    document.body,
  );
};

export default AddSiteModal;
